import { useNotification } from "@hooks/useNotification";
import { APIResponse } from "@models/core";
import { PortfolioInvestmentTypeSelection } from "@models/managed-accounts/entities/step/setup";
import {
  AnalysisDecisionDTO,
  AnalysisDTO,
  AnalysisFilterDTO,
} from "@models/platform-analysis/entities/analysis";
import { AnalysisBannerNameDTO } from "@models/platform-analysis/entities/bannerName";
import { DisclaimerDTO } from "@models/platform-analysis/entities/disclaimer";
import { AnalysisStepDTO } from "@models/platform-analysis/entities/step";
import { BusinessMetricCategories } from "@models/platform-analysis/entities/steps/businessMetric";
import {
  AnalysisConfigurationFeatureSectionDTO,
  AnalysisGetFeatureSectionsDTO,
} from "@models/platform-analysis/entities/steps/feature";
import {
  AnalysisFeeCalculatorDTO,
  AnalysisFeePortfolioDTO,
  FeeSubProductDTO,
  PortfolioAssignmentValidationRequestDTO,
} from "@models/platform-analysis/entities/steps/fee";
import { AnalysisSetupDTO } from "@models/platform-analysis/entities/steps/setup";
import { FeatureAnalysisCollapseStateFilterLevel } from "@models/platform-analysis/enums/feature/featureAnalysisCollapseLevelFilter";
import { FeatureAnalysisEssentialFilterLevel } from "@models/platform-analysis/enums/feature/featureAnalysisEssentialFilter";
import { FeatureAnalysisAvailabilityFilterLevel } from "@models/platform-analysis/enums/feature/featureAnalysisFilter";
import { FeesDisplayMode } from "@models/platform-analysis/enums/fee/displayMode";
import { FeesDisplayStyle } from "@models/platform-analysis/enums/fee/displayStyle";
import { FeesPerformanceScope } from "@models/platform-analysis/enums/fee/feesPerformanceScope";
import { FeeHeatmapMode } from "@models/platform-analysis/enums/fee/heatmapMode";
import { FeeInvestmentMenuOption } from "@models/platform-analysis/enums/fee/investmentMenu";
import { FeeMissingInvestmentsOption } from "@models/platform-analysis/enums/fee/missingInvestments";
import { AnalysisStatus } from "@models/platform-analysis/enums/status";
import { AnalysisStep } from "@models/platform-analysis/enums/step";
import { TableViewMode } from "@models/platform-analysis/enums/tableViewMode";
import {
  checkPlatformAnalysisUniqueNameThunk,
  checkPortfolioAssignmentThunk,
  createPlatformAnalysisPortfolioThunk,
  createPlatformAnalysisThunk,
  disqualifyAllPlatformsThunk,
  getAdviserUsersThunk,
  getAnalysisBannerNameThunk,
  getBusinessMetricSelectionsThunk,
  getFamilyGroupsThunk,
  getFamilyMembersThunk,
  getFeatureSelectionsThunk,
  getFeeCalculatorThunk,
  getPlatformAnalysisOwnersThunk,
  getPlatformAnalysisStepsThunk,
  getPlatformAnalysisThunk,
  loadPlatformAnalysisPlatformThunk,
  resetAnalysisStore,
  resetFee,
  saveAnalysisFiltersThunk,
  setDiscardedStages,
  setFeatureAnalysisRowFilters,
  setFeatureAnalysisTableViewMode,
  setFeeAnalysisDisplayMode,
  setFeeAnalysisDisplayStyle,
  setFeeAnalysisDisqualifiedFilters,
  setFeeAnalysisDisqualifiedHiddenIds,
  setFeeAnalysisFilters,
  setFeeAnalysisHeatmapMode,
  setFeeAnalysisHiddenIds,
  setFeeAnalysisPerformanceScope,
  setSummaryFeeAnalysisFilters,
  setSummaryFeeAnalysisHiddenIds,
  setValidatedPortfolioSetupFirstTime,
  updateAnalysisDecisionThunk,
  updateAnalysisFinalJSONsThunk,
  updateAnalysisSteps,
  updateBannerName,
  updateBusinessMetricSelectionThunk,
  updateDisclaimers,
  updateFeatureSelectionsGroup,
  updateFeatureSelectionThunk,
  updateFeeCalculatorStore,
  updateFeeCalculatorThunk,
  updateIsAccessDenied,
  updateIsCompleted,
  updateIsDirty,
  updateIsReadOnly,
  updateIsShowAnalysisScores,
  updateIsShowHidden,
  updateIsValid,
  updateIsValidPortfolioSetups,
  updateLineChartColorsAction,
  updatePlatformAnalysisPortfolioThunk,
  updatePlatformAnalysisStore,
  updatePlatformAnalysisThunk,
  updateReachAnalysisSetupPage,
  updateSelectedStepIndex,
  updateSelectedSubStepIndex,
  updateShouldShowSaveProgressWarning,
  updateStepHidden,
  updateStepUnHighlighted,
  updateTotalCollapsedBusinessMetricRowsAction,
  updateTotalCollapsedFeatureAnalysisRowsAction,
} from "@redux/slices/platform-analysis";
import { RootState, useAppDispatch } from "@redux/store";
import {
  duplicateAnalysis,
  getDisclaimers,
  putPlatformAnalysisComment,
  putPlatformAnalysisStatus,
} from "@services/platform-analysis/platformAnalysisService";
import { useState } from "react";
import { useSelector } from "react-redux";

const { Feature, BusinessMetric, Fee } = AnalysisStep;
export const usePlatformAnalysis = () => {
  const dispatch = useAppDispatch();
  const { notify } = useNotification();
  const {
    platformAnalysis,
    disclaimers,
    lineChartColors,
    isCompleted,
    isReadOnly,
    analysisPlatforms,
    ui: {
      portfolioLevel,
      reachAnalysisSetupPage,
      isShowAnalysisScores,
      discardedStages,
      isDirty,
      isValid,
      selectedStep,
      isValidPortfolioSetups,
    },
  } = useSelector((state: RootState) => state.platformAnalysis);
  const [isModifyingComment, setIsModifyingComment] = useState(false);

  const setBannerName = (bannerName: AnalysisBannerNameDTO) => {
    dispatch(updateBannerName(bannerName));
  };
  const setPlatformAnalysis = (platformAnalysis?: AnalysisDTO) => {
    dispatch(updatePlatformAnalysisStore(platformAnalysis));
  };
  const setSelectedStepIndex = (stepIndex?: number) => {
    dispatch(updateSelectedStepIndex(stepIndex));
  };
  const setSelectedSubStepIndex = (subStepIndex?: number) => {
    dispatch(updateSelectedSubStepIndex(subStepIndex));
  };
  const setAnalysisSteps = (analysisSteps?: AnalysisStepDTO) => {
    dispatch(updateAnalysisSteps(analysisSteps));
  };
  const setAnalysisDisclaimers = (disclaimers?: DisclaimerDTO[]) => {
    dispatch(updateDisclaimers(disclaimers));
  };
  const setShowSaveProgressWarning = (showWarning: boolean) => {
    dispatch(updateShouldShowSaveProgressWarning(showWarning));
  };
  const setIsValidPortfolioSetups = (isValid: boolean) => {
    dispatch(updateIsValidPortfolioSetups(isValid));
  };

  const setStepUnHighlighted = (
    step: AnalysisStep,
    isUnHighlighted?: boolean,
  ) => {
    dispatch(updateStepUnHighlighted({ step, isUnHighlighted }));
  };

  const setStepHidden = ({
    step,
    isHidden,
  }: {
    step: AnalysisStep;
    isHidden: boolean | undefined;
  }) => {
    dispatch(updateStepHidden({ step, isHidden }));
  };

  const setIsValid = (isValid?: boolean) => {
    dispatch(updateIsValid(isValid));
  };
  const setIsCompleted = (isCompleted?: boolean) => {
    dispatch(updateIsCompleted(isCompleted));
  };
  const setIsReadOnly = (isReadOnly?: boolean) => {
    dispatch(updateIsReadOnly(isReadOnly));
  };

  const setIsAccessDenied = (isAccessDenied?: boolean) => {
    dispatch(updateIsAccessDenied(isAccessDenied));
  };

  const setIsShowHidden = (isShowHidden?: boolean) => {
    dispatch(updateIsShowHidden(isShowHidden));
  };

  const setIsDirty = (isDirty: boolean) => {
    dispatch(updateIsDirty(isDirty));
  };

  const setIsShowAnalysisScores = (isShowScore: boolean) => {
    dispatch(updateIsShowAnalysisScores(isShowScore));
  };

  const setReachAnalysisSetupPage = (status?: "FIRST_TIME" | "SECOND_TIME") => {
    dispatch(updateReachAnalysisSetupPage(status));
  };

  const setTotalCollapsedFeatureAnalysisRows = (total: number) => {
    dispatch(updateTotalCollapsedFeatureAnalysisRowsAction(total));
  };

  const setTotalCollapsedBusinessMetricRows = (total: number) => {
    dispatch(updateTotalCollapsedBusinessMetricRowsAction(total));
  };

  // Line chart colors
  const setLineChartColors = (colors: { [key in string]: string }) => {
    dispatch(updateLineChartColorsAction(colors));
  };

  const resetPlatformAnalysisFee = () => {
    dispatch(resetFee());
  };

  const setAnalysisDiscardedStages = (steps: AnalysisStep[]) => {
    dispatch(setDiscardedStages(steps));
  };

  const updateFeatureAnalysisRowFilters = ({
    collapseLevel,
    essentialLevel,
    availabilityLevel,
    resetCollapseStateChangedManually = false,
  }: {
    collapseLevel?: FeatureAnalysisCollapseStateFilterLevel;
    essentialLevel?: FeatureAnalysisEssentialFilterLevel;
    availabilityLevel?: FeatureAnalysisAvailabilityFilterLevel;
    resetCollapseStateChangedManually?: boolean;
  }) => {
    dispatch(
      setFeatureAnalysisRowFilters({
        collapseLevel,
        essentialLevel,
        availabilityLevel,
        resetCollapseStateChangedManually,
      }),
    );
  };

  const setFeeFilters = (
    investmentMenuValue?: FeeInvestmentMenuOption,
    missingInvestmentValue?: FeeMissingInvestmentsOption,
  ) => {
    dispatch(
      setFeeAnalysisFilters({ investmentMenuValue, missingInvestmentValue }),
    );
  };

  const setSummaryFeeFilters = ({
    investmentMenuValue,
    missingInvestmentValue,
    sortedSubProducts,
  }: {
    investmentMenuValue?: FeeInvestmentMenuOption;
    missingInvestmentValue?: FeeMissingInvestmentsOption;
    sortedSubProducts: FeeSubProductDTO[];
  }) => {
    dispatch(
      setSummaryFeeAnalysisFilters({
        investmentMenuValue,
        missingInvestmentValue,
        sortedSubProducts,
      }),
    );
  };

  const setDisqualifiedFeeFilter = (
    investmentMenuValue?: FeeInvestmentMenuOption,
    missingInvestmentValue?: FeeMissingInvestmentsOption,
  ) => {
    dispatch(
      setFeeAnalysisDisqualifiedFilters({
        investmentMenuValue,
        missingInvestmentValue,
      }),
    );
  };

  const setHiddenIds = (data: string[]) => {
    dispatch(setFeeAnalysisHiddenIds(data));
  };

  const setSummaryHiddenIds = (
    hiddenIds: string[],
    sortedSubProducts: FeeSubProductDTO[],
  ) => {
    dispatch(setSummaryFeeAnalysisHiddenIds({ hiddenIds, sortedSubProducts }));
  };

  const setDisqualifiedHiddenIds = (data: string[]) => {
    dispatch(setFeeAnalysisDisqualifiedHiddenIds(data));
  };

  const setHeatmapMode = (data: FeeHeatmapMode) => {
    dispatch(setFeeAnalysisHeatmapMode(data));
  };

  const setDisplayStyle = (data: FeesDisplayStyle) => {
    dispatch(setFeeAnalysisDisplayStyle(data));
  };

  const setPerformanceScope = (data: FeesPerformanceScope) => {
    dispatch(setFeeAnalysisPerformanceScope(data));
  };

  const setDisplayMode = (data: FeesDisplayMode) => {
    dispatch(setFeeAnalysisDisplayMode(data));
  };

  const setInitialValidatedPortfolioSetup = (isValid: boolean) => {
    dispatch(setValidatedPortfolioSetupFirstTime(isValid));
  };

  const setSummaryFeatureAnalysisTableViewMode = (mode: TableViewMode) => {
    dispatch(setFeatureAnalysisTableViewMode(mode));
  };

  function handleResponseData<T>({
    response,
    customSuccessMessage,
    showSuccessMessage = false,
    customErrorMessage,
    isVoidResponse = false,
    onSuccess,
  }: {
    response: APIResponse<T>;
    showSuccessMessage?: boolean;
    customSuccessMessage?: string;
    customErrorMessage?: string;
    isVoidResponse?: boolean;
    onSuccess?: () => void;
  }): T | undefined {
    if (!response.isSuccess || (!response.data && !isVoidResponse)) {
      notify(customErrorMessage ?? response.message, {
        variant: "error",
        close: true,
      });

      return;
    }

    if (showSuccessMessage) {
      notify(customSuccessMessage ?? response.message, {
        variant: "success",
        close: true,
      });
    }

    onSuccess && onSuccess();

    return response.data;
  }

  function handleRawResponseData<T>({
    response,
    showErrorMessage = false,
  }: {
    response: APIResponse<T>;
    showErrorMessage?: boolean;
  }): APIResponse<T> {
    if (!response.isSuccess || !response.data) {
      showErrorMessage &&
        notify(response.message, {
          variant: "error",
          close: true,
        });
    }

    return response;
  }

  const createNewPlatformAnalysis = async (setupStep: AnalysisSetupDTO) => {
    const response = await dispatch(
      createPlatformAnalysisThunk(setupStep),
    ).unwrap();

    return handleResponseData({ response, showSuccessMessage: true });
  };

  const updateCurrentPlatformAnalysis = async (
    platformAnalysis: AnalysisDTO,
    isShowMessage = true,
  ) => {
    const response = await dispatch(
      updatePlatformAnalysisThunk(platformAnalysis),
    ).unwrap();

    return handleResponseData({ response, showSuccessMessage: isShowMessage });
  };

  const loadPlatformAnalysis = async (params: {
    platformAnalysisId: string;
    stepId: AnalysisStep;
  }) => {
    const response = await dispatch(getPlatformAnalysisThunk(params)).unwrap();

    return handleResponseData({
      response,
      onSuccess: () => setStepUnHighlighted(params.stepId, false),
    });
  };

  const loadPlatformAnalysisDisclaimers = async (
    platformAnalysisId: string,
  ) => {
    const response = await getDisclaimers(platformAnalysisId);

    return handleResponseData({ response });
  };

  const loadAnalysisBannerName = async (platformAnalysisId: string) => {
    const response = await dispatch(
      getAnalysisBannerNameThunk(platformAnalysisId),
    ).unwrap();
    setIsAccessDenied(response.isForbidden);
    if (response.isForbidden) return;

    return handleResponseData({ response });
  };

  const loadPlatformAnalysisStep = async (platformAnalysisId: string) => {
    const response = await dispatch(
      getPlatformAnalysisStepsThunk(platformAnalysisId),
    ).unwrap();

    return handleResponseData({ response });
  };

  const setFeatureSelections = (
    selections: AnalysisConfigurationFeatureSectionDTO[],
  ) => {
    dispatch(updateFeatureSelectionsGroup(selections));
  }; // Feature Analysis
  const loadFeatureSelections = async (params: {
    platformAnalysisId: string;
  }) => {
    const response = await dispatch(getFeatureSelectionsThunk(params)).unwrap();

    return handleResponseData({
      response,
      onSuccess: () => setStepUnHighlighted(Feature, false),
    });
  };

  const updateFeatureSelections = async (params: {
    platformAnalysisId: string;
    featureSelections: AnalysisGetFeatureSectionsDTO;
  }) => {
    const response = await dispatch(
      updateFeatureSelectionThunk(params),
    ).unwrap();

    return !!handleResponseData({ response, showSuccessMessage: true });
  };

  // Business metric
  const loadBusinessMetricSelections = async (params: {
    platformAnalysisId: string;
  }) => {
    const response = await dispatch(
      getBusinessMetricSelectionsThunk(params),
    ).unwrap();

    return handleResponseData({
      response,
      onSuccess: () => setStepUnHighlighted(BusinessMetric, false),
    });
  };

  const updateBusinessMetricSelections = async (params: {
    platformAnalysisId: string;
    businessMetricSelections: BusinessMetricCategories;
  }) => {
    const response = await dispatch(
      updateBusinessMetricSelectionThunk(params),
    ).unwrap();

    return !!handleResponseData({ response, showSuccessMessage: true });
  };

  // Fee
  const setFeeCalculator = (fee?: AnalysisFeeCalculatorDTO) => {
    dispatch(updateFeeCalculatorStore(fee));
  };

  const loadFeeCalculator = async (params: { platformAnalysisId: string }) => {
    const response = await dispatch(getFeeCalculatorThunk(params)).unwrap();

    return handleResponseData({
      response,
      onSuccess: () => setStepUnHighlighted(Fee, false),
    });
  };

  const updateFeeCalculator = async (params: {
    platformAnalysisId: string;
    formData: AnalysisFeeCalculatorDTO;
  }) => {
    const response = await dispatch(updateFeeCalculatorThunk(params)).unwrap();

    return !!handleResponseData({ response, showSuccessMessage: true });
  };

  // Portfolio
  const createNewPlatformAnalysisFeePortfolio = async (
    platformAnalysisId: string,
    formData: AnalysisFeePortfolioDTO,
  ): Promise<AnalysisFeePortfolioDTO | null> => {
    const response = await dispatch(
      createPlatformAnalysisPortfolioThunk({ platformAnalysisId, formData }),
    ).unwrap();

    return handleResponseData({ response, showSuccessMessage: true }) ?? null;
  };

  const updatePlatformAnalysisFeePortfolio = async (
    platformAnalysisId: string,
    formData: AnalysisFeePortfolioDTO,
  ): Promise<AnalysisFeePortfolioDTO | null> => {
    const response = await dispatch(
      updatePlatformAnalysisPortfolioThunk({ platformAnalysisId, formData }),
    ).unwrap();

    return handleResponseData({ response, showSuccessMessage: true }) ?? null;
  };

  const updatePlatformAnalysisStatus = async (
    platformAnalysisId: string,
    status: AnalysisStatus,
  ) => {
    const response = await putPlatformAnalysisStatus(
      platformAnalysisId,
      status,
    );

    return handleResponseData({
      response,
      showSuccessMessage: true,
      customSuccessMessage:
        status === AnalysisStatus.Archived
          ? "Analysis archived."
          : "Analysis restored.",
    });
  };

  const modifyComment = async (
    platformAnalysisId: string,
    productId: string,
    stepId: AnalysisStep,
    comment: string | null,
  ) => {
    setIsModifyingComment(true);
    const response = await putPlatformAnalysisComment({
      comment: comment || "",
      platformAnalysisId,
      productId,
      stepId,
    });
    setIsModifyingComment(false);

    return handleResponseData({ response, showSuccessMessage: true });
  };

  const resetStore = () => {
    dispatch(resetAnalysisStore());
  };

  const duplicatePlatformAnalysis = async (platformAnalysisId: string) => {
    const response = await duplicateAnalysis(platformAnalysisId);

    return handleResponseData({ response, showSuccessMessage: true });
  };

  const checkAnalysisPortfolioAssignment = async (
    platformAnalysisId: string,
    data: PortfolioAssignmentValidationRequestDTO,
    investmentTypeSelection: PortfolioInvestmentTypeSelection,
  ) => {
    const response = await dispatch(
      checkPortfolioAssignmentThunk({
        platformAnalysisId,
        data,
        investmentTypeSelection,
      }),
    ).unwrap();

    return handleResponseData({ response });
  };

  const getPlatformAnalysisOwners = async (name?: string) => {
    const response = await dispatch(
      getPlatformAnalysisOwnersThunk(name),
    ).unwrap();

    return handleResponseData({ response });
  };

  const checkPlatformAnalysisUniqueName = async ({
    firmId,
    name,
    ownerId,
    id,
  }: {
    firmId: string;
    name: string;
    ownerId: string;
    id?: string;
  }) => {
    const response = await dispatch(
      checkPlatformAnalysisUniqueNameThunk({ firmId, name, ownerId, id }),
    ).unwrap();

    return handleRawResponseData({ response });
  };

  const loadPlatformAnalysisProducts = async ({
    platformAnalysisId,
    filterByProductName,
    configurationVersion,
    apiPath,
  }: {
    platformAnalysisId: string;
    filterByProductName?: string;
    configurationVersion?: number;
    apiPath?: string;
  }) => {
    const response = await dispatch(
      loadPlatformAnalysisPlatformThunk({
        platformAnalysisId,
        filterByProductName,
        configurationVersion,
        apiPath,
      }),
    ).unwrap();

    return handleResponseData({ response });
  };

  const getFamilyMembers = async (
    platformAnalysisId: string,
    familyGroupId: string,
  ) => {
    const response = await dispatch(
      getFamilyMembersThunk({ platformAnalysisId, familyGroupId }),
    ).unwrap();

    return handleResponseData({ response });
  };

  const getFamilyGroups = async (
    platformAnalysisId: string,
    adviserUserId: string,
    name: string,
  ) => {
    const response = await dispatch(
      getFamilyGroupsThunk({ platformAnalysisId, adviserUserId, name }),
    ).unwrap();

    return handleResponseData({ response });
  };

  const getAdviserUsers = async (platformAnalysisId: string, name: string) => {
    const response = await dispatch(
      getAdviserUsersThunk({ platformAnalysisId, name }),
    ).unwrap();

    return handleResponseData({ response });
  };

  const updateDecision = async ({
    analysisId,
    analysisStep,
    productId,
    decisionStatus,
    comment,
  }: AnalysisDecisionDTO) => {
    const response = await dispatch(
      updateAnalysisDecisionThunk({
        analysisId,
        analysisStep,
        productId,
        decisionStatus,
        comment,
      }),
    ).unwrap();

    return handleResponseData({ response });
  };

  const disqualifyAllPlatforms = async ({
    analysisId,
    analysisStep,
  }: {
    analysisId: string;
    analysisStep: AnalysisStep;
  }) => {
    const response = await dispatch(
      disqualifyAllPlatformsThunk({ analysisId, stepId: analysisStep }),
    ).unwrap();

    handleResponseData({
      response,
      isVoidResponse: true,
      customErrorMessage: "Failed to disqualify all platforms",
    });

    return response.isSuccess;
  };

  const saveAnalysisFilters = async (
    analysisId: string,
    filters: AnalysisFilterDTO,
  ) => {
    const response = await dispatch(
      saveAnalysisFiltersThunk({ analysisId, filters }),
    ).unwrap();

    return !!handleResponseData({
      response,
      customErrorMessage: "Failed to save filters",
    });
  };

  const updateFinalJSONs = async (analysisId: string) => {
    const response = await dispatch(
      updateAnalysisFinalJSONsThunk({ analysisId }),
    ).unwrap();

    return !!handleResponseData({ response });
  };

  return {
    isDirty,
    isValid,
    disclaimers,
    lineChartColors,
    isCompleted,
    isReadOnly,
    isModifyingComment,
    isShowAnalysisScores,
    platformAnalysis,
    discardedStages,
    analysisPlatforms,
    selectedStep,
    isValidPortfolioSetups,
    portfolioLevel,
    setSelectedStepIndex,
    setSelectedSubStepIndex,
    setIsCompleted,
    setIsReadOnly,
    setIsValid,
    setIsAccessDenied,
    setIsShowHidden,
    setIsDirty,
    setIsShowAnalysisScores,
    setShowSaveProgressWarning,
    setIsValidPortfolioSetups,
    setBannerName,
    setAnalysisSteps,
    setStepHidden,
    setPlatformAnalysis,
    setStepUnHighlighted,
    setFeatureSelections,
    createNewPlatformAnalysis,
    updateCurrentPlatformAnalysis,
    loadPlatformAnalysis,
    loadPlatformAnalysisDisclaimers,
    loadFeatureSelections,
    loadBusinessMetricSelections,
    updateFeatureSelections,
    updateBusinessMetricSelections,
    setFeeCalculator,
    loadFeeCalculator,
    updateFeeCalculator,
    loadAnalysisBannerName,
    loadPlatformAnalysisStep,
    resetStore,
    updatePlatformAnalysisStatus,
    modifyComment,
    reachAnalysisSetupPage,
    setReachAnalysisSetupPage,
    setAnalysisDisclaimers,
    setTotalCollapsedFeatureAnalysisRows,
    setTotalCollapsedBusinessMetricRows,
    setLineChartColors,
    duplicatePlatformAnalysis,
    resetPlatformAnalysisFee,
    createNewPlatformAnalysisFeePortfolio,
    checkAnalysisPortfolioAssignment,
    updatePlatformAnalysisFeePortfolio,
    getPlatformAnalysisOwners,
    checkPlatformAnalysisUniqueName,
    setAnalysisDiscardedStages,
    loadPlatformAnalysisProducts,
    getFamilyMembers,
    getFamilyGroups,
    getAdviserUsers,
    updateDecision,
    updateFeatureAnalysisRowFilters,
    disqualifyAllPlatforms,
    saveAnalysisFilters,
    setFeeFilters,
    setDisqualifiedFeeFilter,
    setHiddenIds,
    setSummaryHiddenIds,
    setDisqualifiedHiddenIds,
    setHeatmapMode,
    setDisplayStyle,
    setPerformanceScope,
    setDisplayMode,
    setInitialValidatedPortfolioSetup,
    updateFinalJSONs,
    setSummaryFeatureAnalysisTableViewMode,
    setSummaryFeeFilters,
  };
};
